/**
 * ChauparStatisticsService — Records game results and statistics
 */

import { StatisticsManager } from '../../services/StatisticsManager';
import {
  ChauparGameResult,
  ChauparStatistics,
  ChauparGameStats,
  ChauparDifficulty,
} from '../types';

export class ChauparStatisticsService {
  private stats: ChauparStatistics;
  private storageKey = 'chaupar_statistics';

  constructor() {
    this.stats = this.loadStats();
  }

  recordResult(result: ChauparGameResult): void {
    this.stats.totalGames++;
    this.stats.totalPlayTime += result.duration;

    const isWin = result.winningTeam === 'teamA'; // Human is always on Team A in single player

    if (result.mode === 'singleplayer' && result.difficulty) {
      this.updateStats(this.stats.singlePlayer[result.difficulty], isWin, result);
    } else if (result.mode === 'local-multiplayer') {
      this.updateStats(this.stats.localMultiplayer, isWin, result);
    } else if (result.mode === 'multiplayer-realtime') {
      this.updateStats(this.stats.onlineMultiplayer, isWin, result);
    }

    this.saveStats();
  }

  getStats(): ChauparStatistics {
    return { ...this.stats };
  }

  getStatsForDifficulty(difficulty: ChauparDifficulty): ChauparGameStats {
    return { ...this.stats.singlePlayer[difficulty] };
  }

  private updateStats(stats: ChauparGameStats, isWin: boolean, result: ChauparGameResult): void {
    if (isWin) {
      stats.wins++;
    } else {
      stats.losses++;
    }
    const totalGames = stats.wins + stats.losses;
    stats.winPercentage = totalGames > 0 ? (stats.wins / totalGames) * 100 : 0;
    stats.averageGameLength = (stats.averageGameLength * (totalGames - 1) + result.duration) / totalGames;
    stats.averageCapturesPerGame = (stats.averageCapturesPerGame * (totalGames - 1) + result.capturesMade) / totalGames;
  }

  private loadStats(): ChauparStatistics {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore parse errors
    }
    return this.createEmptyStats();
  }

  private saveStats(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.stats));
    } catch {
      // Ignore storage errors
    }
  }

  private createEmptyStats(): ChauparStatistics {
    const emptyGameStats: ChauparGameStats = {
      wins: 0,
      losses: 0,
      winPercentage: 0,
      averageGameLength: 0,
      averageCapturesPerGame: 0,
    };

    return {
      singlePlayer: {
        easy: { ...emptyGameStats },
        medium: { ...emptyGameStats },
        hard: { ...emptyGameStats },
      },
      localMultiplayer: { ...emptyGameStats },
      onlineMultiplayer: { ...emptyGameStats },
      totalGames: 0,
      totalPlayTime: 0,
    };
  }
}
